import { Box, Grid } from '@mui/material';
import React, { FC, useMemo, useRef, useState } from 'react';

import 'swiper/css';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { SliderItemProvider, SliderProvider } from '@/data/context/content';

type Props = {
	slides?: JSX.Element[];
	children?: JSX.Element[];
	id?: string;
	withoutmargin?: boolean;
};

export const CarouselSlider: FC<Props> = ({ id, slides, withoutmargin = false, children }) => {
	const _slides = useMemo(() => {
		if (slides) return slides;
		const cleanChildren = React.Children.toArray(children).filter((child) =>
			React.isValidElement(child)
		);
		return cleanChildren;
	}, [slides, children]);

	const [activeSlide, setActiveSlide] = useState(0);
	const swiperRef = useRef<SwiperType | null>(null);
	const [mouseMoved, setMouseMoved] = useState(false);

	if (_slides.length === 1) {
		return (
			<SliderProvider value={{ mouseMoved, activeSlide }}>
				<Box
					id={id}
					width="100vw"
					minWidth="100%"
					maxWidth="100%"
					marginBottom={withoutmargin ? '' : { xs: '20px', sm: '40px' }}
				>
					{_slides}
				</Box>
			</SliderProvider>
		);
	}

	return (
		<Box
			id={id}
			width="100vw"
			minWidth="100%"
			maxWidth="100%"
			position="relative"
			marginBottom={withoutmargin ? '' : { xs: '20px', sm: '40px' }}
			sx={{ overflowX: 'hidden' }}
		>
			<SliderProvider value={{ mouseMoved, activeSlide }}>
				<Swiper
					modules={[Autoplay]}
					loop={true}
					autoplay={{
						delay: 8000,
						disableOnInteraction: false,
					}}
					slidesPerView={1}
					onSwiper={(swiper: any) => {
						swiperRef.current = swiper;
					}}
					onSlideChange={(swiper: any) => {
						setActiveSlide(swiper?.realIndex);
					}}
					onSliderMove={() => {
						setMouseMoved(true);
					}}
					onTouchEnd={() => {
						setTimeout(() => setMouseMoved(false), 50);
					}}
				>
					{_slides.map((slide, index) => (
						<SwiperSlide key={`id_carousel_${index}`}>
							<SliderItemProvider key={`id_carousel_${index}`} value={{ index }}>
								<Grid>{slide}</Grid>
							</SliderItemProvider>
						</SwiperSlide>
					))}
				</Swiper>
			</SliderProvider>
			{_slides.length > 1 ? (
				<Box
					width="100vw"
					minWidth="100vw"
					maxWidth="100vw"
					position={'absolute'}
					justifyContent={'center'}
					display={'inline-flex'}
					bottom={'20px'}
					zIndex={'20'}
				>
					<Box
						sx={{
							display: 'flex',
							direction: 'row',
							gap: '0px !important',
							borderRadius: '10px',
							//backgroundColor: '#FFFFFF80',
						}}
					>
						{Array.from({ length: _slides.length }).map((_, index) => (
							<Box
								key={`id_carousel_dot_${index}`}
								sx={{
									backgroundColor: 'white',
									width: {
										xs: `calc(150px/${_slides.length})`,
										md: `calc(300px/${_slides.length})`,
									},
									height: '6px',
									margin: '0px !important',
									padding: '0px !important',
									opacity: activeSlide === index ? '1 !important' : '0.5',
									borderTopLeftRadius: index === 0 ? '10px' : '0px',
									borderBottomLeftRadius: index === 0 ? '10px' : '0px',
									borderTopRightRadius: index === _slides.length - 1 ? '10px' : '0px',
									borderBottomRightRadius: index === _slides.length - 1 ? '10px' : '0px',
									cursor: 'pointer',
									marginLeft: index !== 0 ? '6px !important' : '0px',
									boxShadow:
										'0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)',
								}}
								onClick={(event) => {
									event.stopPropagation();
									swiperRef.current?.slideToLoop(index);
								}}
							/>
						))}
					</Box>
				</Box>
			) : null}
		</Box>
	);
};
